import * as moment from 'moment-timezone'

const ABBR_REGEX = new RegExp(`^[^-+]`) // For non-empty abbreviations that do not begin with -/+
const SLASH_REGEX = new RegExp(`\/`) // Ex. EST/Eastern is not supported by the backend
const ETC_REGEX = new RegExp(`Etc\/`) // Ex. ETC/GMT+1 is also not supported by the backend

export const MOST_POPULATED_TIMEZONES = Object.freeze(
  [
    {
      label: "Pacific/Midway (SST) -11:00",
      value: "Pacific/Midway"
    },
    {
      label: "Pacific/Honolulu (HST) -10:00",
      value: "Pacific/Honolulu"
    },
    {
      label: "Pacific/Marquesas (MART) -09:30",
      value: "Pacific/Marquesas"
    },
    {
      label: "America/Adak (HDT) -09:00",
      value: "America/Adak"
    },
    {
      label: "America/Anchorage (AKDT) -08:00",
      value: "America/Anchorage"
    },
    {
      label: "America/Los_Angeles (PDT) -07:00",
      value: "America/Los_Angeles"
    },
    {
      label: "America/Denver (MDT) -06:00",
      value: "America/Denver"
    },
    {
      label: "America/Mexico_City (CDT) -05:00",
      value: "America/Mexico_City"
    },
    {
      label: "America/Caracas (VET) -04:30",
      value: "America/Caracas"
    },
    {
      label: "America/New_York (EST) -04:00",
      value: "America/New_York"
    },
    {
      label: "America/Sao_Paulo (BRT) -03:00",
      value: "America/Sao_Paulo"
    },
    {
      label: "America/St_Johns (NDT) -02:30",
      value: "America/St_Johns"
    },
    {
      label: "America/Godthab (WGST) -02:00",
      value: "America/Godthab"
    },
    {
      label: "Atlantic/Cape_Verde (CVT) -01:00",
      value: "Atlantic/Cape_Verde"
    },
    {
      label: "Africa/Abidjan (GMT) +00:00",
      value: "Africa/Abidjan"
    },
    {
      label: "Africa/Bangui (WAT) +01:00",
      value: "Africa/Bangui"
    },
    {
      label: "Europe/London (GST) +01:00",
      value: "Europe/London"
    },
    {
      label: "Africa/Cairo (EET) +02:00",
      value: "Africa/Cairo"
    },
    {
      label: "Asia/Jerusalem (GMT) +03:00",
      value: "Asia/Jerusalem"
    },
    {
      label: "Europe/Moscow (MSK) +03:00",
      value: "Europe/Moscow"
    },
    {
      label: "Asia/Dubai (GST) +04:00",
      value: "Asia/Dubai"
    },
    {
      label: "Asia/Tehran (IRDT) +04:30",
      value: "Asia/Tehran"
    },
    {
      label: "Asia/Karachi (PKT) +05:00",
      value: "Asia/Karachi"
    },
    {
      label: "Asia/Calcutta (IST) +05:30",
      value: "Asia/Calcutta"
    },
    {
      label: "Asia/Kathmandu (NPT) +05:45",
      value: "Asia/Kathmandu"
    },
    {
      label: "Asia/Dacca (BDT) +06:00",
      value: "Asia/Dacca"
    },
    {
      label: "Asia/Rangoon (MMT) +06:30",
      value: "Asia/Rangoon"
    },
    {
      label: "Asia/Jakarta (WIB) +07:00",
      value: "Asia/Jakarta"
    },
    {
      label: "Asia/Manila (PHT) +08:00",
      value: "Asia/Manila"
    },
    {
      label: "Asia/Pyongyang (KST) +08:30",
      value: "Asia/Pyongyang"
    },
    {
      label: "Australia/Eucla (ACWST) +08:45",
      value: "Australia/Eucla"
    },
    {
      label: "Asia/Tokyo (JST) +09:00",
      value: "Asia/Tokyo"
    },
    {
      label: "Australia/Adelaide (ACST) +09:30",
      value: "Australia/Adelaide"
    },
    {
      label: "Australia/ACT (AEST) +10:00",
      value: "Australia/ACT"
    },
    {
      label: "Australia/LHI (LHST) +10:30",
      value: "Australia/LHI"
    },
    {
      label: "Asia/Sakhalin (SAKT) +11:00",
      value: "Asia/Sakhalin"
    },
    {
      label: "Antarctica/McMurdo (NZST) +12:00",
      value: "Antarctica/McMurdo"
    },
    {
      label: "Pacific/Chatham (CHAST) +12:45",
      value: "Pacific/Chatham"
    },
    {
      label: "Pacific/Tongatapu (TOT) +13:00",
      value: "Pacific/Tongatapu"
    },
    {
      label: "Pacific/Kiritimati (LINT) +14:00",
      value: "Pacific/Kiritimati"
    }
  ])

export const getAllTimezoneOptions = () => {
  return moment.tz.names()
  .filter(timezone => SLASH_REGEX.test(timezone) && !ETC_REGEX.test(timezone))
  .map(timezone => {
    const metadata = getTimezoneMetadata(timezone, new Date())
    return toTimezoneOption(metadata)
  })
}

export const getCurrentTimezoneOption = () => {
  const timezoneGuess = moment.tz.guess()
  const timezoneMetadata = getTimezoneMetadata(timezoneGuess, new Date())
  return {
    label: `🔎 Current Timezone (${timezoneMetadata.name})`,
    value: timezoneGuess
  }
}

export const getOptionForTimezone = (timezone) => {
  const timezoneMetadata = getTimezoneMetadata(timezone, new Date())
  return toTimezoneOption(timezoneMetadata)
}

const toTimezoneOption = ({ name, abbreviation, offsetAsString }) => {
  return {
    label: `${name}${abbreviation ? ` (${abbreviation})` : ""} ${offsetAsString}`,
    value: name
  }
}

const getTimezoneMetadata = (timezone, date) => {
  const timestamp = date.getTime()
  const zone = moment.tz.zone(timezone)
  const zonedDate = moment.tz(timestamp, timezone)
  const offsetAsString = zonedDate.format('Z')
  const abbr = zone.abbr(timestamp)

  return {
    name: zone.name,
    abbreviation: ABBR_REGEX.test(abbr) ? abbr : undefined,
    offset: zonedDate.utcOffset(),
    offsetAsString: offsetAsString,
    population: zone.population
  }
}


// WEBPACK FOOTER //
// ./src/scripts/components/settings/ChatWidget/components/HoursAndTargeting/TimezonePicker/utils.js